import { useCallback } from 'react'
import CRMIntegrationService from '@forwardflow/services/CRMIntegrationServices'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { UserService } from '@forwardflow/services/UserService'
import { useAppStore, useOnboardingStoreV2 } from '@forwardflow/store'
import {
   CONNECT_ASSISTANT_PATH,
   peekHandle,
} from '@forwardflow/services/AssistantConnect'
import { useNavigate } from 'react-router-dom'

interface UseUserNavigationParams {
   user?: {
      email?: string
      userOrganizations?: Array<{ organizationId: string }>
   }
   currentOrgId?: string
   searchParams?: string
   shouldNotNavigateToDashboard?: boolean
   redirectTo?: string
}

/**
 * True when the wizard in THIS session marked the given organization's
 * onboarding complete (the customer finished setup). Session-scoped on
 * purpose: on a fresh sign-in the store is empty and the backend flag
 * decides, as before.
 */
const finishedOnboardingHere = (organizationId: string): boolean => {
   try {
      const { onboardingData } = useOnboardingStoreV2.getState()
      return (
         Boolean(onboardingData?.isOnBoardingCompleted) &&
         onboardingData?.organizationId === organizationId
      )
   } catch {
      // No wizard store in this context (tests mock the store module).
      return false
   }
}

/**
 * Custom hook to determine and navigate to the appropriate route based on user state.
 * This hook encapsulates the logic for:
 * - Getting the current organization ID based on user type (operator vs regular user)
 * - Checking if user is an operator
 * - Checking organization status
 * - Validating onboarding completion via 24-hour delay
 * - Navigating to the correct destination
 */
export const useUserNavigation = () => {
   const { setCurrentOrganization, currentOrganization } = useAppStore()
   const navigate = useNavigate()
   /**
    * Gets the current organization ID for a user.
    * For operators: checks organizationList from API
    * For regular users: checks user.userOrganizations
    * Prefers stored org ID from localStorage if it exists in the list,
    * otherwise returns the first available org.
    */
   const getCurrentOrgId = useCallback(async (): Promise<
      string | undefined
   > => {
      const userData = await UserService.getUserData()
      const storedOrgId = currentOrganization
      const isOperator = isOperatorEmail(userData?.email || '')

      // Helper to get valid operator org ID
      const getOperatorOrgId = async () => {
         const organizationList = await OrganizationService.getOrganization({
            page: 1,
            limit: 10,
         })
         if (!organizationList?.length) return undefined

         // Return stored org if it exists in the list
         if (
            storedOrgId &&
            organizationList.some((org) => org.id === storedOrgId)
         ) {
            return storedOrgId
         }

         // Fallback to first available org
         return organizationList[0].id
      }

      // Helper to get valid user org ID
      const getUserOrgId = async () => {
         const userOrgs = userData?.userOrganizations || []
         if (!userOrgs?.length) return undefined

         // Return stored org if it exists in user's orgs
         if (
            storedOrgId &&
            userOrgs.some((org) => org.organizationId === storedOrgId)
         ) {
            return storedOrgId
         }

         // Fallback to first available org
         return userOrgs[0].organizationId
      }

      return isOperator ? await getOperatorOrgId() : await getUserOrgId()
   }, [currentOrganization])

   /**
    * Determines the correct navigation path for a user and navigates there.
    * Returns the path it navigated to for use in cases where you need the path.
    */
   const navigateUser = useCallback(
      async ({
         currentOrgId,
         searchParams = '',
         shouldNotNavigateToDashboard,
         redirectTo,
      }: UseUserNavigationParams): Promise<string> => {
         const search = searchParams || ''
         const navigateToOnboarding = () => {
            // A repeated redirect must not erase an active handoff's router state.
            if (window.location.pathname !== '/onboarding') navigate('/onboarding')
            return '/onboarding'
         }

         /**
          * 0. An assistant connection wins over ordinary routing.
          *
          * The user came here to connect something. Sending them to their
          * dashboard or onboarding first means they land somewhere familiar
          * having apparently connected nothing, and the handle expires.
          *
          * Peeked, not consumed: the route below does the consuming read.
          */
         const assistantHandle = peekHandle(search)
         if (assistantHandle) {
            const destination = `${CONNECT_ASSISTANT_PATH}?handle=${encodeURIComponent(assistantHandle)}`
            navigate(destination)
            return destination
         }

         // 1. If user has no organizations, redirect to onboarding
         if (!currentOrgId) {
            return navigateToOnboarding()
         }

         // Set the current org in the store up-front so every org-scoped API
         // call below is routed correctly — the X-ORG header is read from the
         // store (services/Axios/configs.ts), and calls like
         // getAgentOnboardingStatus() don't take an explicit orgId. Without this,
         // a fresh login (store cleared on logout) sends an empty X-ORG and the
         // gateway misroutes ff-be calls to ff-internal-be (404).
         setCurrentOrganization(currentOrgId)

         // 2. If user has organizations, check 24-hour delay status
         const res = await CRMIntegrationService.get24HourDelay(currentOrgId)

         // If onboarding not complete (24-hour delay not disabled), go to onboarding
         // — unless this session just finished setup for this very
         // organization. The staff flag can lag the customer by a day, and
         // bouncing a customer who has just finished back into the wizard is
         // a dead end, not a safeguard.
         if (!res?.isDisabled && !finishedOnboardingHere(currentOrgId)) {
            setCurrentOrganization(currentOrgId)
            return navigateToOnboarding()
         }

         // Check if Agent with incomplete onboarding
         const userRoleData = await UserService.getUserRoleInOrganization(currentOrgId)
         if (userRoleData?.roles?.includes('Agent')) {
            const status = await UserService.getAgentOnboardingStatus()
            if (!status?.agentOnboardingCompleted) {
               setCurrentOrganization(currentOrgId)
               return navigateToOnboarding()
            }
         }

         if (!shouldNotNavigateToDashboard) {
            setCurrentOrganization(currentOrgId)
            let destination = redirectTo || `/dashboard${search}`
            if (destination.startsWith('/onboarding')) {
               destination = `/dashboard${search}`
            }
            window.location.href = destination
            return destination
         }

         return ''
      },
      [setCurrentOrganization, navigate]
   )

   /**
    * Gets the appropriate path without navigating.
    * Useful for conditional logic or previewing where user would be sent.
    */
   const getUserPath = useCallback(
      async ({
         user,
         currentOrgId,
         searchParams = '',
      }: UseUserNavigationParams): Promise<string> => {
         const search = searchParams || ''

         // Allow operators to navigate to dashboard
         const isOperator = isOperatorEmail(user?.email || '')
         if (isOperator) {
            return `/dashboard${search}`
         }

         // 1. If user has no organizations, redirect to onboarding
         if (!currentOrgId) {
            return '/onboarding'
         }

         // 2. If user has organizations, check 24-hour delay status
         const res = await CRMIntegrationService.get24HourDelay(currentOrgId)

         // If onboarding not complete (24-hour delay not disabled), go to onboarding
         if (!res?.isDisabled && !finishedOnboardingHere(currentOrgId)) {
            return '/onboarding'
         }

         // Otherwise, go to dashboard (onboarding is complete)
         return `/dashboard${search}`
      },
      []
   )

   return { navigateUser, getUserPath, getCurrentOrgId }
}
